/**
 * Garage Requests View
 * Shows user's submitted feature requests
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, ThumbsUp, MessageCircle, X, Send, CheckCircle, Clock, Zap, AlertCircle, Users, TrendingUp, ExternalLink, FileCode, Rocket, Link2, Wrench, Sparkles, Pin, Lightbulb, Check, ChevronLeft, ChevronRight, Search, BarChart3 } from 'lucide-react';
import RequestsDashboard, { matchesAttentionFilter } from '../components/RequestsDashboard';
import '../components/RequestsWorkspace.css';
import garageAPI from '../../../services/garage/api';
import config from '../../../config/environment';
import { PERSONA_OPTIONS } from '../constants/formOptions';
import {
  canManageRequests as roleCanManageRequests,
  getAdminPersonaScopes,
} from '../constants/roleAccess';
import RequestInfoModal from '../modals/RequestInfoModal';
import RouteToITModal from '../modals/RouteToITModal';
import EscalateToDotModal, { getEscalateJob } from '../modals/EscalateToDotModal';
import PublishGemModal from '../modals/PublishGemModal';
import GarageLoadingScreen from '../components/GarageLoadingScreen';
import WorkflowReviewerCard from '../components/WorkflowReviewerCard';
import ConfirmModal from '../modals/ConfirmModal';
import AlertModal from '../modals/AlertModal';
import TourGuide from '../components/TourGuide';
import { REQUESTS_TOUR_STEPS } from '../tours/requestsTour';
import { formatTimestamp, getTimezoneAbbreviation, parseTimestamp } from '../../../utils/dateFormatter';
import { formatCommentHtml } from '../../../utils/formatCommentHtml';

// Component to handle async timestamp formatting
const TimestampDisplay = ({ timestamp, userEmail, showTime = false, showTimezone = false, showRelative = false }) => {
  const [formatted, setFormatted] = useState('');

  useEffect(() => {
    const format = async () => {
      const result = await formatTimestamp(timestamp, {
        showTime,
        showTimezone,
        showRelative,
        userEmail
      });
      setFormatted(result);
    };
    if (timestamp) {
      format();
    }
  }, [timestamp, userEmail, showTime, showTimezone, showRelative]);

  return formatted || parseTimestamp(timestamp).toLocaleDateString();
};

// Status buckets for Unresolved filter + default list sort
const OPEN_STATUSES = ['submitted', 'under_review', 'needs_info'];
const IN_PROGRESS_STATUSES = [
  'in_progress',
  'optimization',
  'it_review',
  'deploying',
  'escalated_to_it_bizops',
];
const CLOSED_STATUSES = ['published', 'rejected'];

/**
 * Statuses that pause the pipeline instead of advancing it. Index of the stage
 * the request is held at, used only when the API timeline is unavailable.
 */
const PAUSED_STATUS_STAGE = {
  needs_info: 1, // Under review
  on_hold: 1,
  rejected: 1,
};

/** Default list order: open → in progress → other → published/rejected. Then newest first. */
const requestSortRank = (status) => {
  if (OPEN_STATUSES.includes(status)) return 0;
  if (IN_PROGRESS_STATUSES.includes(status)) return 1;
  if (CLOSED_STATUSES.includes(status)) return 3;
  return 2; // on_hold, etc.
};

const compareRequestsDefault = (a, b) => {
  const rankDiff = requestSortRank(a.status) - requestSortRank(b.status);
  if (rankDiff !== 0) return rankDiff;
  const aTime = a.createdAt ? parseTimestamp(a.createdAt).getTime() : 0;
  const bTime = b.createdAt ? parseTimestamp(b.createdAt).getTime() : 0;
  return bTime - aTime;
};

const matchesStatusFilter = (idea, statusFilter) => {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'unresolved') return !CLOSED_STATUSES.includes(idea.status);
  return idea.status === statusFilter;
};

const GarageRequestsView = ({ userEmail, userName, isAdmin, userRole, accessPersona, accessPersonas = [], onNavigate, setDetailViewState, onNavigateToRequestDetail, initialTab, showOnboarding = false, onboardingChecked = true }) => {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(
    ['admin-queue', 'dashboard'].includes(initialTab) ? initialTab : 'my-requests'
  ); // 'my-requests' or 'admin-queue'
  const [pendingQueueFilters, setPendingQueueFilters] = useState(null);
  const [attentionFilter, setAttentionFilter] = useState('all');
  const [loadError, setLoadError] = useState('');
  const loadVersion = useRef(0);
  const [stats, setStats] = useState({ open: 0, inProgress: 0, published: 0, needsAction: 0, total: 0 });

  // Check if user can manage requests (admin, reviewer, or Persona Admin).
  const adminPersonaScopes = getAdminPersonaScopes(
    userRole,
    accessPersonas,
    accessPersona
  );
  const canManageRequests = isAdmin
    || roleCanManageRequests(userRole, adminPersonaScopes);

  useEffect(() => {
    if (!canManageRequests && activeTab !== 'my-requests') setActiveTab('my-requests');
  }, [canManageRequests, activeTab]);

  // Both tabs start unfiltered so no request is hidden by its status.
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [appTypeFilter, setAppTypeFilter] = useState('all');
  const [personaFilter, setPersonaFilter] = useState(
    'all'
  );
  const [personaOptions, setPersonaOptions] = useState(PERSONA_OPTIONS);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Detail view state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetails, setRequestDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Request Info modal state
  const [requestInfoModalOpen, setRequestInfoModalOpen] = useState(false);
  const [requestInfoIdea, setRequestInfoIdea] = useState(null);

  // Route to IT modal state
  const [routeToITModalOpen, setRouteToITModalOpen] = useState(false);
  // Confirm and Alert modals
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'default' });
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', variant: 'info' });

  const [routeToITIdea, setRouteToITIdea] = useState(null);

  // Escalate to DOT modal state
  const [escalateDotModalOpen, setEscalateDotModalOpen] = useState(false);
  const [escalateDotIdea, setEscalateDotIdea] = useState(null);
  const [pendingDotReviewIdeaId, setPendingDotReviewIdeaId] = useState(null);

  // Publish Gem modal state
  const [publishGemModalOpen, setPublishGemModalOpen] = useState(false);
  const [publishGemIdea, setPublishGemIdea] = useState(null);

  // Tour state — start after onboarding so they never overlap
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (!onboardingChecked || showOnboarding) {
      setShowTour(false);
      return;
    }
    const tourDone = localStorage.getItem('tour-completed-requests') === 'true';
    setShowTour(!tourDone);
  }, [onboardingChecked, showOnboarding]);

  useEffect(() => {
    loadIdeas();
    loadStats();
    setSearchQuery('');
    setAttentionFilter('all');
    setCurrentPage(1);
    setStatusFilter('all');
  }, [activeTab]);

  // Restore tab when returning from request detail (Admin queue vs My requests)
  useEffect(() => {
    if (['admin-queue', 'my-requests', 'dashboard'].includes(initialTab) && (canManageRequests || initialTab === 'my-requests')) {
      setActiveTab(initialTab);
    }
  }, [initialTab, canManageRequests]);

  // Reset to first page when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, priorityFilter, appTypeFilter, personaFilter, searchQuery, itemsPerPage, attentionFilter]);

  // Keep URL in sync with active tab (so refresh / back restore Admin queue)
  useEffect(() => {
    const path = window.location.pathname;
    if (!path.includes('/ai-garage/requests')) return;
    const url = activeTab !== 'my-requests'
      ? `/ai-garage/requests?tab=${activeTab}`
      : '/ai-garage/requests';
    window.history.replaceState({ view: 'requests', tab: activeTab }, '', url);
  }, [activeTab]);

  useEffect(() => {
    const loadPersonaOptions = async () => {
      try {
        const options = await garageAPI.getConfigOptions('persona');
        if (options?.length) {
          setPersonaOptions(options.map((opt) => opt.label));
        }
      } catch (error) {
        console.error('Failed to load persona options:', error);
      }
    };
    loadPersonaOptions();
  }, []);

  useEffect(() => {
    setPersonaFilter('all');
  }, [accessPersonas, accessPersona, userRole]);

  useEffect(() => {
    if (selectedRequest) {
      loadRequestDetails(selectedRequest);
    }
  }, [selectedRequest]);

  const loadIdeas = async () => {
    const version = ++loadVersion.current;
    try {
      setLoading(true);
      setLoadError('');
      // Load ideas based on active tab
      let data;
      if (activeTab === 'my-requests' || !canManageRequests) {
        // User's own requests
        data = await garageAPI.getIdeasByUser(userEmail);
      } else {
        if (adminPersonaScopes.length) {
          data = await garageAPI.getIdeas();
        } else {
          data = await garageAPI.getIdeas();
        }
      }
      if (version === loadVersion.current) setIdeas(data);
    } catch (error) {
      if (version === loadVersion.current) {
        setIdeas([]);
        setLoadError('Requests could not be loaded. Please try again.');
      }
      console.error('Failed to load ideas:', error);
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Load stats based on active tab
      const userEmailParam = activeTab === 'my-requests' || !canManageRequests ? userEmail : null;
      const statsData = await garageAPI.getIdeaStats(userEmailParam);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadRequestDetails = async (requestId) => {
    try {
      setDetailLoading(true);
      const data = await garageAPI.getIdea(requestId);
      setRequestDetails(data);
      // Load comments if available
      // setComments(data.comments || []);
      setComments([]); // Placeholder until backend supports comments

      // Set header back button
      if (setDetailViewState) {
        setDetailViewState({
          isDetailView: true,
          backLabel: 'Back to Requests',
          onBack: closeRequestDetail
        });
      }
    } catch (error) {
      console.error('Failed to load request details:', error);
      setRequestDetails(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleVote = async (ideaId) => {
    try {
      // Simple toggle upvote - no vote type needed
      await garageAPI.voteIdea(ideaId, { userEmail, voteType: 'upvote' });
      await loadIdeas(); // Reload to get updated scores
      // If we're viewing this request in detail, reload it too
      if (selectedRequest === ideaId) {
        await loadRequestDetails(ideaId);
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handleStatusChange = async (ideaId, newStatus, e) => {
    if (e) {
      e.stopPropagation(); // Prevent card click
    }

    // Confirm rejection
    if (newStatus === 'rejected') {
      setConfirmModal({
        isOpen: true,
        title: 'Reject Request',
        message: 'Are you sure you want to reject this request? This action cannot be undone.',
        variant: 'danger',
        onConfirm: async () => {
          try {
            await garageAPI.updateIdea(ideaId, { status: newStatus });
            await loadIdeas();
            if (selectedRequest === ideaId) {
              await loadRequestDetails(ideaId);
            }
            setConfirmModal({ ...confirmModal, isOpen: false });
          } catch (error) {
            console.error('Failed to update status:', error);
            setConfirmModal({ ...confirmModal, isOpen: false });
            setAlertModal({
              isOpen: true,
              title: 'Error',
              message: 'Failed to update status. Please try again.',
              variant: 'error'
            });
          }
        }
      });
      return;
    }

    // For non-rejection status changes
    try {
      await garageAPI.updateIdea(ideaId, { status: newStatus });
      await loadIdeas(); // Reload to get updated data
      if (selectedRequest === ideaId) {
        await loadRequestDetails(ideaId);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update status. Please try again.',
        variant: 'error'
      });
    }
  };

  const handleRouteToIT = (idea, e) => {
    if (e) {
      e.stopPropagation(); // Prevent card click
    }
    setRouteToITIdea(idea);
    setRouteToITModalOpen(true);
  };

  const canEscalateToDot = (idea) =>
    config.DOT_ESCALATION_ENABLED &&
    canManageRequests &&
    ['admin', 'analyst_reviewer', 'persona_admin'].includes(String(userRole || '').toLowerCase()) &&
    idea?.primaryPersona === 'Sales';

  const canPublishGem = (idea) =>
    config.PUBLISH_GEM_ENABLED &&
    canManageRequests &&
    (idea?.appType || '').toLowerCase() === 'gem' &&
    (idea?.status || '').toLowerCase() === 'deploying';

  const isGemPublished = (idea) =>
    (idea?.status || '').toLowerCase() === 'published' && Boolean(idea?.existingToolId);

  const handleEscalateToDot = (idea, e) => {
    if (e) e.stopPropagation();
    setEscalateDotIdea(idea);
    setEscalateDotModalOpen(true);
  };

  const handlePublishGem = (idea, e) => {
    if (e) e.stopPropagation();
    setPublishGemIdea(idea);
    setPublishGemModalOpen(true);
  };

  // Reopen review when a background assist job finishes
  useEffect(() => {
    if (!pendingDotReviewIdeaId) return undefined;
    const timer = setInterval(() => {
      const job = getEscalateJob(pendingDotReviewIdeaId);
      if (job?.status === 'ready') {
        clearInterval(timer);
        const idea = ideas.find((i) => i.id === pendingDotReviewIdeaId);
        if (idea) {
          setEscalateDotIdea(idea);
          setEscalateDotModalOpen(true);
        }
        setPendingDotReviewIdeaId(null);
        setAlertModal({
          isOpen: true,
          title: 'DOT draft ready',
          message: 'AI Intake Assist finished. Review and submit the draft to IT BizOps.',
          variant: 'info',
        });
      } else if (job?.status === 'done') {
        clearInterval(timer);
        setPendingDotReviewIdeaId(null);
        loadIdeas();
        loadStats();
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [pendingDotReviewIdeaId, ideas]);

  const handleRequestClick = (requestId) => {
    // Navigate to the dedicated detail view page; remember which tab to return to
    if (onNavigateToRequestDetail) {
      onNavigateToRequestDetail(requestId, { tab: activeTab });
    } else {
      // Fallback to inline detail view
      setSelectedRequest(requestId);
    }
  };

  const closeRequestDetail = () => {
    setSelectedRequest(null);
    setRequestDetails(null);
    setComments([]);
    setNewComment('');
    // Clear header back button
    if (setDetailViewState) {
      setDetailViewState({ isDetailView: false, backLabel: '', onBack: null });
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmittingComment(true);
      // TODO: Implement comment API when backend supports it
      // await garageAPI.addComment(selectedRequest, {
      //   userEmail,
      //   userName: userName || userEmail?.split('@')[0],
      //   text: newComment
      // });
      // await loadRequestDetails(selectedRequest);
      setNewComment('');
      setAlertModal({
        isOpen: true,
        title: 'Coming Soon',
        message: 'Comment feature coming soon!',
        variant: 'info'
      });
    } catch (error) {
      console.error('Failed to submit comment:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to submit comment. Please try again.',
        variant: 'error'
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  // Helper: Format time ago
  const getTimeAgo = (dateString) => {
    const date = parseTimestamp(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return `${Math.floor(diffInSeconds / 2592000)}mo ago`;
  };

  // Helper: Get workflow stages (fallback when API timeline is absent)
  const getWorkflowStages = (idea) => {
    const stages = [
      { id: 'submitted', label: 'Submitted' },
      { id: 'under_review', label: 'Under review' },
      { id: 'in_progress', label: 'In progress' },
      { id: 'optimization', label: 'Optimization' },
      { id: 'escalated_to_it_bizops', label: 'IT BizOps' },
      { id: 'it_review', label: 'IT review' },
      { id: 'deploying', label: 'Deploying' },
      { id: 'published', label: 'Published' }
    ];
    const isSales = (idea?.primaryPersona || '') === 'Sales';
    const onBizOps = (idea?.status || '') === 'escalated_to_it_bizops';
    if (isSales || onBizOps) return stages;
    return stages.filter((s) => s.id !== 'escalated_to_it_bizops');
  };

  // Helper: Get current stage index
  const getStageIndex = (status, idea) => {
    const stages = getWorkflowStages(idea);
    // Treat legacy routed-to-it as IT review stage
    const normalized =
      status === 'routed-to-it' || status === 'it-review' ? 'it_review' : status;
    const index = stages.findIndex(stage => stage.id === normalized);
    if (index >= 0) return index;
    // Paused statuses sit alongside the pipeline; hold the rail at the stage
    // the request had reached rather than blanking it.
    return PAUSED_STATUS_STAGE[normalized] ?? -1;
  };

  // Prefer API workflowTimeline (visit-based on backend). Never invent "completed"
  // for stages never entered (Route to IT / BizOps are side branches).
  const resolveWorkflowStages = (idea) => {
    const stages = getWorkflowStages(idea);
    if (Array.isArray(idea?.workflowTimeline) && idea.workflowTimeline.length > 0) {
      const byId = Object.fromEntries(idea.workflowTimeline.map((s) => [s.id, s]));
      return stages.map((stage) => {
        const api = byId[stage.id];
        return {
          id: stage.id,
          label: stage.label || api?.label || stage.id,
          state: api?.state || 'pending',
          reviewerData: api?.reviewerData || null,
        };
      });
    }
    const currentStageIndex = getStageIndex(idea?.status, idea);
    return stages.map((stage, index) => ({
      ...stage,
      state:
        currentStageIndex >= 0 && index === currentStageIndex
          ? 'active'
          : stage.id === 'submitted' && currentStageIndex > 0
            ? 'completed'
            : 'pending',
      reviewerData: null,
    }));
  };

  // Helper: Get priority badge class
  const getPriorityClass = (priority) => {
    const classes = {
      low: 'garage-priority-low',
      normal: 'garage-priority-normal',
      high: 'garage-priority-high',
      urgent: 'garage-priority-urgent'
    };
    return classes[priority] || classes.normal;
  };

  // Helper: Get priority label
  const getPriorityLabel = (priority) => {
    const labels = {
      low: 'Low priority',
      normal: 'Normal priority',
      high: 'High priority',
      urgent: 'Urgent'
    };
    return labels[priority] || labels.normal;
  };

  // Helper: Get reach label
  const getReachLabel = (reach) => {
    const labels = {
      my_team: 'My team (under 10)',
      department: 'Department (10-50)',
      function: 'Function (50-500)',
      company: 'Company-wide (500+)'
    };
    return labels[reach] || reach;
  };

  // Helper: Get value driver label
  const getValueDriverLabel = (valueDriver) => {
    const labels = {
      save_time: 'Save time',
      drive_revenue: 'Drive revenue',
      reduce_cost: 'Reduce cost',
      reduce_risk: 'Reduce risk',
      better_experience: 'Better experience'
    };
    return labels[valueDriver] || valueDriver.replace(/_/g, ' ');
  };

  // Helper: Format request phase label
  const getRequestPhaseLabel = (phase) => {
    const labels = {
      'active-development': 'Active Development Phase',
      'existing-enhancement': 'Feature / Optimization Request',
      'ideation': 'Ideation & Design Phase'
    };
    return labels[phase] || phase;
  };

  // Helper: Format app type label
  const getAppTypeLabel = (appType) => {
    if (appType === 'gem') return 'Gem';
    if (appType === 'app') return 'App';
    return null;
  };

  const appTypeOptions = [
    { value: 'all', label: 'All types' },
    { value: 'gem', label: 'Gem' },
    { value: 'app', label: 'App' },
    { value: 'unset', label: 'Ideation / Not set' }
  ];

  // Filter options
  const statusOptions = [
    { value: 'unresolved', label: 'Unresolved' },
    { value: 'all', label: 'All statuses' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'under_review', label: 'Under review' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'optimization', label: 'Optimization' },
    { value: 'escalated_to_it_bizops', label: 'Escalated to IT Business Operations' },
    { value: 'it_review', label: 'IT review' },
    { value: 'deploying', label: 'Deploying' },
    { value: 'published', label: 'Published' },
    { value: 'needs_info', label: 'Needs info' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'on_hold', label: 'On hold' }
  ];

  // Helper: Friendly status label (dropdown / badge)
  const getStatusLabel = (status) => {
    const match = statusOptions.find((opt) => opt.value === status);
    if (match) return match.label;
    return (status || 'pending').replace(/_/g, ' ');
  };

  const priorityOptions = [
    { value: 'all', label: 'All priorities' },
    { value: 'high', label: 'High' },
    { value: 'normal', label: 'Medium' },
    { value: 'low', label: 'Low' },
    { value: 'urgent', label: 'Urgent' }
  ];

  // Apply filters + search, then default sort (open first, published/rejected last)
  const filteredIdeas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = ideas.filter((idea) => {
      const matchesStatus = matchesStatusFilter(idea, statusFilter) && matchesAttentionFilter(idea, attentionFilter);
      const matchesPriority = priorityFilter === 'all' || idea.priority === priorityFilter;
      const matchesPersona = personaFilter === 'all' || (idea.primaryPersona || idea.personas?.[0]) === personaFilter;
      const matchesAppType =
        appTypeFilter === 'all' ||
        (appTypeFilter === 'unset' && !idea.appType) ||
        idea.appType === appTypeFilter;

      if (!(matchesStatus && matchesPriority && matchesPersona && matchesAppType)) {
        return false;
      }
      if (!q) return true;

      const hay = [
        idea.title,
        idea.description,
        idea.requesterEmail,
        idea.requesterName,
        idea.id,
        idea.category,
        idea.appType,
        idea.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });

    return [...filtered].sort(attentionFilter === 'oldest' ? (a, b) => parseTimestamp(a.createdAt).getTime() - parseTimestamp(b.createdAt).getTime() : compareRequestsDefault);
  }, [
    ideas,
    statusFilter,
    priorityFilter,
    personaFilter,
    appTypeFilter,
    searchQuery,
    attentionFilter,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredIdeas.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedIdeas = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return filteredIdeas.slice(start, start + itemsPerPage);
  }, [filteredIdeas, safePage, itemsPerPage]);

  const searchPlaceholder =
    activeTab === 'admin-queue'
      ? 'Search by title, requester, email…'
      : 'Search my requests…';

  const openQueue = (filters = {}) => {
    setActiveTab('admin-queue');
    // The tab-change effect resets filters; apply drill-down after the new tab mounts.
    setPendingQueueFilters(filters);
  };

  useEffect(() => {
    if (activeTab === 'admin-queue' && pendingQueueFilters) {
      setPriorityFilter('all');
      setSearchQuery('');
      setCurrentPage(1);
      setStatusFilter(pendingQueueFilters.status || 'all');
      setPersonaFilter(pendingQueueFilters.persona || 'all');
      setAppTypeFilter(pendingQueueFilters.type || 'all');
      setAttentionFilter(pendingQueueFilters.attention || 'all');
      setPendingQueueFilters(null);
    }
  }, [activeTab, pendingQueueFilters]);

  return (
    <div className="garage-content garage-requests-workspace">
      {!selectedRequest ? (
        <>
          {/* Requests List View */}
          <div className="garage-page-hero">
            <div>
              <h1 className="garage-page-title">Requests</h1>
              <p className="garage-page-subtitle">Submit and track feature requests</p>
            </div>
            <div className="garage-hero-actions">
              <button
                className="garage-btn garage-btn-secondary"
                onClick={() => setShowTour(true)}
                title="Take a quick tour"
              >
                <Sparkles size={16} />
                Help
              </button>
              <button className="garage-btn garage-btn-primary" data-tour="requests-submit" onClick={() => onNavigate('request-flow')} title="Submit a feature request">
                <Plus size={16} />
                Submit new request
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="garage-tabs garage-requests-tabs" data-tour="requests-tabs">
            <button
              className={`garage-tab ${activeTab === 'my-requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('my-requests')}
            >
              My requests
              {activeTab === 'my-requests' && stats.total > 0 && (
                <span className="garage-tab-badge">{stats.total}</span>
              )}
            </button>
            {canManageRequests && (
              <button
                className={`garage-tab ${activeTab === 'admin-queue' ? 'active' : ''}`}
                onClick={() => setActiveTab('admin-queue')}
              >
                Admin queue
                {activeTab === 'admin-queue' && stats.total > 0 && (
                  <span className="garage-tab-badge">{stats.total}</span>
                )}
              </button>
            )}
            {canManageRequests && (
              <button className={`garage-tab requests-dashboard-tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
                <BarChart3 size={16} /> Dashboard
              </button>
            )}
          </div>

          {activeTab === 'dashboard' && canManageRequests ? (
            loading ? <GarageLoadingScreen message="Loading dashboard..." /> : loadError ? (
              <div className="garage-empty" role="alert"><h3>{loadError}</h3><button className="garage-btn garage-btn-secondary" onClick={loadIdeas}>Try again</button></div>
            ) : <RequestsDashboard ideas={ideas} scopes={adminPersonaScopes} onOpenQueue={openQueue} onOpenRequest={handleRequestClick} getStatusLabel={getStatusLabel} />
          ) : (<>
          {/* Stats Cards */}
          <div className="garage-stats-grid">
            <div className="garage-stat-card">
              <Clock className="garage-stat-icon" size={20} />
              <div className="garage-stat-content">
                <div className="garage-stat-number">{stats.open}</div>
                <div className="garage-stat-label">Open</div>
              </div>
            </div>
            <div className="garage-stat-card">
              <Zap className="garage-stat-icon" size={20} />
              <div className="garage-stat-content">
                <div className="garage-stat-number">{stats.inProgress}</div>
                <div className="garage-stat-label">In progress</div>
              </div>
            </div>
            <div className="garage-stat-card">
              <CheckCircle className="garage-stat-icon" size={20} />
              <div className="garage-stat-content">
                <div className="garage-stat-number">{stats.published}</div>
                <div className="garage-stat-label">Published</div>
              </div>
            </div>
            {canManageRequests && activeTab === 'admin-queue' && (
              <div className="garage-stat-card garage-stat-card-attention">
                <AlertCircle className="garage-stat-icon" size={20} />
                <div className="garage-stat-content">
                  <div className="garage-stat-number">{stats.needsAction}</div>
                  <div className="garage-stat-label">Needs action</div>
                </div>
              </div>
            )}
          </div>

          {/* Persona Admin Filter Notice */}
          {activeTab === 'admin-queue' && adminPersonaScopes.length > 0 && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#1565c0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}><Pin size={16} /></span>
              <span>
                <strong>{adminPersonaScopes.join(', ')} Admin View:</strong> Showing only requests for your assigned personas
              </span>
            </div>
          )}

          {attentionFilter !== 'all' && (
            <div className="requests-active-filter">Showing: {({ review: 'Awaiting review', progress: 'In progress', unassigned: 'Unassigned unresolved', oldest: 'Unresolved, oldest first' })[attentionFilter]}
              <button onClick={() => setAttentionFilter('all')} aria-label="Clear attention filter"><X size={14} /></button>
            </div>
          )}
          {/* Filters + search/pagination — above the list */}
          <div className="garage-filters garage-requests-controls">
            <div className="garage-requests-search-wrap">
              <Search size={16} className="garage-requests-search-icon" aria-hidden />
              <input
                type="text"
                className="garage-requests-search"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search requests"
              />
            </div>

            <div className="garage-filter-right">
              <span className="garage-total-count">
                {filteredIdeas.length} result{filteredIdeas.length !== 1 ? 's' : ''}
              </span>

              {activeTab === 'admin-queue' && (
                <>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="garage-filter-dropdown"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={personaFilter}
                    onChange={(e) => setPersonaFilter(e.target.value)}
                    className="garage-filter-dropdown"
                    disabled={adminPersonaScopes.length === 1}
                  >
                    {adminPersonaScopes.length ? (
                      <>
                        <option value="all">All assigned personas</option>
                        {adminPersonaScopes.map((persona) => (
                          <option key={persona} value={persona}>{persona}</option>
                        ))}
                      </>
                    ) : (
                      <>
                        <option value="all">All personas</option>
                        {personaOptions.map((persona) => (
                          <option key={persona} value={persona}>{persona}</option>
                        ))}
                      </>
                    )}
                  </select>

                  <select
                    value={appTypeFilter}
                    onChange={(e) => setAppTypeFilter(e.target.value)}
                    className="garage-filter-dropdown"
                  >
                    {appTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="garage-filter-dropdown"
                  >
                    {priorityOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="garage-filter-dropdown garage-page-size-select"
                aria-label="Results per page"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>

              {filteredIdeas.length > itemsPerPage && (
                <div className="garage-requests-pager">
                  <button
                    type="button"
                    className="garage-requests-pager-btn"
                    disabled={safePage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="garage-requests-pager-label">
                    {safePage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="garage-requests-pager-btn"
                    disabled={safePage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="garage-tools-section">
        {loading ? (
          <GarageLoadingScreen message="Loading requests..." />
        ) : loadError ? (
          <div className="garage-empty" role="alert"><h3>{loadError}</h3><button className="garage-btn garage-btn-secondary" onClick={loadIdeas}>Try again</button></div>
        ) : filteredIdeas.length === 0 ? (
          <div className="garage-empty">
            <div className="garage-empty-icon"><Lightbulb size={40} /></div>
            <h3>{searchQuery.trim() || attentionFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all' || appTypeFilter !== 'all' || personaFilter !== 'all'
              ? 'No matching requests'
              : 'No requests yet'}</h3>
            <p>
              {searchQuery.trim() || attentionFilter !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all' || appTypeFilter !== 'all'
                ? 'Try adjusting search or filters'
                : 'Submit your first feature request to get started'}
            </p>
            {!searchQuery.trim() && attentionFilter === 'all' && statusFilter === 'all' && priorityFilter === 'all' && appTypeFilter === 'all' && (
              <button className="garage-btn garage-btn-primary" onClick={() => onNavigate('request-flow')}>
                <Plus size={16} />
                Submit request
              </button>
            )}
          </div>
        ) : (
          <div className="garage-requests-list" data-tour="requests-list">
            {paginatedIdeas.map(idea => {
              const stages = resolveWorkflowStages(idea);

              return (
              <div
                key={idea.id}
                className={`garage-request-card-enhanced ${idea.status === 'rejected' ? 'rejected' : ''}`}
                onClick={() => handleRequestClick(idea.id)}
              >
                {/* Card Header */}
                <div className="garage-request-card-header">
                  <div className="garage-request-header-left">
                    <div className="garage-request-title-row">
                      <h3 className="garage-request-title-enhanced">{idea.title}</h3>
                      {getAppTypeLabel(idea.appType) && (
                        <span className={`garage-app-type-badge garage-app-type-${idea.appType}`}>
                          {getAppTypeLabel(idea.appType)}
                        </span>
                      )}
                    </div>
                    <div className="garage-request-meta-row">
                      <span className="garage-request-requester">
                        {idea.requesterName || idea.requesterEmail}
                      </span>
                      {idea.category && (
                        <>
                          <span className="garage-meta-separator">·</span>
                          <span className="garage-badge-category">{idea.category}</span>
                        </>
                      )}
                      <span className="garage-meta-separator">·</span>
                      <span className="garage-time-ago">
                        Submitted <TimestampDisplay timestamp={idea.createdAt} userEmail={userEmail} showRelative={true} />
                      </span>
                    </div>
                  </div>
                  <span className={`garage-status-badge garage-status-${idea.status}`}>
                    {getStatusLabel(idea.status)}
                  </span>
                </div>

                {/* Description */}
                <p className="garage-request-description-enhanced">{idea.description}</p>

                {(idea.dotPublicUrl || idea.dotRefNumber) && (
                  <div
                    className="garage-dot-link-banner garage-dot-link-banner-card"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <strong>IT BizOps request:</strong>{' '}
                    {idea.dotRefNumber || 'DOT'}
                    {idea.dotPublicUrl && (
                      <>
                        {' — '}
                        <a
                          href={idea.dotPublicUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View public status <ExternalLink size={12} />
                        </a>
                      </>
                    )}
                  </div>
                )}

                {/* Workflow Timeline */}
                <div className="garage-workflow-timeline">
                  {stages.map((stage, index) => {
                    const stageState = stage.state || 'pending';
                    const next = stages[index + 1];
                    const pathContinues =
                      stageState === 'completed' &&
                      next &&
                      (next.state === 'completed' || next.state === 'active');
                    const hasReviewerCard = Boolean(stage.reviewerData);
                    return (
                      <div
                        key={stage.id}
                        className={`garage-workflow-stage ${stageState}${
                          pathContinues ? ' path-continues' : ''
                        }${hasReviewerCard ? ' has-reviewer-card' : ''}`}
                      >
                        <div className="garage-workflow-stage-dot"></div>
                        <div className="garage-workflow-stage-label">{stage.label}</div>
                        {hasReviewerCard && (
                          <WorkflowReviewerCard reviewerData={stage.reviewerData} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Audience Information */}
                {(idea.personas?.length > 0 || idea.reach || idea.frequency || idea.valueDriver) && (
                  <div className="garage-request-audience-info">
                    {idea.personas?.length > 0 && (
                      <span className="garage-audience-item">
                        <Users size={14} />
                        {idea.personas.join(', ')}
                      </span>
                    )}
                    {idea.reach && (
                      <span className="garage-audience-item">
                        <TrendingUp size={14} />
                        {getReachLabel(idea.reach)}
                      </span>
                    )}
                    {idea.frequency && (
                      <span className="garage-audience-item">
                        <Clock size={14} />
                        {idea.frequency}
                      </span>
                    )}
                    {idea.valueDriver && (
                      <span className="garage-audience-item">
                        <Zap size={14} />
                        {getValueDriverLabel(idea.valueDriver)}
                      </span>
                    )}
                  </div>
                )}

                {/* Assigned Maker */}
                {idea.assignedMakerEmail && (
                  <div className="garage-request-maker-info">
                    <span className="garage-maker-label">Assigned to:</span>
                    <span className="garage-maker-name">{idea.assignedMakerEmail}</span>
                  </div>
                )}

                {/* Additional Context */}
                {idea.implementationNotes && (
                  <div className="garage-request-additional-context">
                    <div className="garage-context-label">Additional context:</div>
                    <p className="garage-context-text">{idea.implementationNotes}</p>
                  </div>
                )}

                {/* Card Footer */}
                <div className="garage-request-card-footer">
                  <div className="garage-request-footer-left">
                    {idea.priority && idea.priority !== 'normal' && (
                      <span className={`garage-badge-priority ${getPriorityClass(idea.priority)}`}>
                        {getPriorityLabel(idea.priority)}
                      </span>
                    )}
                    <div className="garage-request-votes-mini" onClick={(e) => e.stopPropagation()}>
                      <button
                        className={`garage-vote-btn-mini ${idea.upvoters?.includes(userEmail) ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVote(idea.id);
                        }}
                        title={idea.upvoters?.includes(userEmail) ? 'Remove your like' : 'Like this request'}
                      >
                        <ThumbsUp size={12} />
                        <span>{idea.score || 0}</span>
                      </button>
                    </div>
                  </div>

                  {/* Action buttons - Different for admin queue vs my requests */}
                  {activeTab === 'admin-queue' ? (
                    <div className="garage-request-actions">
                      {canEscalateToDot(idea) && (
                        <button
                          className={`garage-action-btn ${idea.dotPublicUuid || idea.status === 'escalated_to_it_bizops' ? 'garage-action-btn-routed' : 'garage-action-btn-dot'}`}
                          onClick={(e) => handleEscalateToDot(idea, e)}
                          title={idea.dotPublicUuid ? 'View / reopen DOT escalation' : 'Escalate to IT BizOps'}
                        >
                          {idea.dotPublicUuid || idea.status === 'escalated_to_it_bizops'
                            ? (<>✓ Escalated to IT BizOps</>)
                            : 'Escalate to IT BizOps'}
                        </button>
                      )}
                      <button
                        className={`garage-action-btn ${idea.status === 'it_review' || idea.status === 'routed-to-it' ? 'garage-action-btn-routed' : 'garage-action-btn-route'}`}
                        onClick={(e) => handleRouteToIT(idea, e)}
                        disabled={idea.status === 'it_review' || idea.status === 'routed-to-it'}
                      >
                        {idea.status === 'it_review' || idea.status === 'routed-to-it' ? (
                          <><Check size={14} /> Routed to IT</>
                        ) : 'Route to IT'}
                      </button>
                      {canPublishGem(idea) && (
                        isGemPublished(idea) ? (
                          <button
                            className="garage-action-btn garage-action-btn-published"
                            disabled
                            onClick={(e) => e.stopPropagation()}
                            title="Already published to Discover"
                          >
                            <Check size={14} /> Published
                          </button>
                        ) : (
                          <button
                            className="garage-action-btn garage-action-btn-publish"
                            onClick={(e) => handlePublishGem(idea, e)}
                            title="Publish this Gem to Discover"
                          >
                            <Rocket size={14} /> Publish
                          </button>
                        )
                      )}
                      <button
                        className="garage-action-btn garage-action-btn-info"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRequestInfoIdea(idea);
                          setRequestInfoModalOpen(true);
                        }}
                      >
                        Request info
                      </button>
                      <button
                        className="garage-action-btn garage-action-btn-reject"
                        onClick={(e) => handleStatusChange(idea.id, 'rejected', e)}
                      >
                        Reject
                      </button>
                    </div>
                  ) : (
                    <button
                      className="garage-btn-view-details"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRequestClick(idea.id);
                      }}
                    >
                      View details →
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
          </div>
          </>)}
        </>
      ) : (
        /* Request Detail View - Replaces the list */
        <div className="garage-request-detail-view-replacement">
          <div className="garage-page-hero">
            <button
              className="garage-btn garage-btn-secondary"
              onClick={closeRequestDetail}
            >
              <X size={16} />
              Back to Requests
            </button>
          </div>

          {detailLoading ? (
            <GarageLoadingScreen message="Loading request details..." />
          ) : !requestDetails ? (
            <div className="garage-empty">
              <h3>Request not found</h3>
              <button className="garage-btn garage-btn-primary" onClick={closeRequestDetail}>
                Back to Requests
              </button>
            </div>
          ) : (
            <div className="garage-request-detail">
              {/* Request Header */}
              <div className="garage-request-detail-header">
                <div className="garage-request-detail-vote">
                  <button
                    className={`garage-vote-btn garage-vote-btn-large ${requestDetails.upvoters?.includes(userEmail) ? 'active' : ''}`}
                    onClick={() => handleVote(requestDetails.id)}
                    title={requestDetails.upvoters?.includes(userEmail) ? 'Remove your like' : 'Like this request'}
                  >
                    <ThumbsUp size={24} />
                    <span className="garage-vote-count-large">{requestDetails.score || 0}</span>
                  </button>
                </div>

                <div className="garage-request-detail-info">
                  <div className="garage-request-title-row">
                    <h1 className="garage-page-title">{requestDetails.title}</h1>
                    {getAppTypeLabel(requestDetails.appType) && (
                      <span className={`garage-app-type-badge garage-app-type-${requestDetails.appType}`}>
                        {getAppTypeLabel(requestDetails.appType)}
                      </span>
                    )}
                  </div>
                  <div className="garage-request-meta">
                    <span className={`garage-request-status garage-request-status-${requestDetails.status}`}>
                      {getStatusLabel(requestDetails.status)}
                    </span>
                    <span className="garage-request-author">
                      By {requestDetails.requesterEmail}
                    </span>
                    <span className="garage-request-date">
                      <TimestampDisplay timestamp={requestDetails.createdAt} userEmail={userEmail} showTime={true} showTimezone={true} />
                    </span>
                  </div>
                </div>
              </div>

              {/* Request Description */}
              <div className="garage-request-detail-description">
                <h3>Description</h3>
                <p>{requestDetails.description}</p>
              </div>

              {/* Request Details Section */}
              {(requestDetails.personas?.length > 0 || requestDetails.reach || requestDetails.frequency || requestDetails.valueDriver) && (
                <div className="garage-request-detail-section">
                  <h3>Request Details</h3>
                  <div className="garage-detail-grid">
                    {requestDetails.personas?.length > 0 && (
                      <div className="garage-detail-item">
                        <div className="garage-detail-label">
                          <Users size={16} />
                          Target Personas
                        </div>
                        <div className="garage-detail-value">{requestDetails.personas.join(', ')}</div>
                      </div>
                    )}
                    {requestDetails.reach && (
                      <div className="garage-detail-item">
                        <div className="garage-detail-label">
                          <TrendingUp size={16} />
                          Expected Reach
                        </div>
                        <div className="garage-detail-value">{getReachLabel(requestDetails.reach)}</div>
                      </div>
                    )}
                    {requestDetails.frequency && (
                      <div className="garage-detail-item">
                        <div className="garage-detail-label">
                          <Clock size={16} />
                          Frequency
                        </div>
                        <div className="garage-detail-value">{requestDetails.frequency}</div>
                      </div>
                    )}
                    {requestDetails.valueDriver && (
                      <div className="garage-detail-item">
                        <div className="garage-detail-label">
                          <Zap size={16} />
                          Business Value
                        </div>
                        <div className="garage-detail-value">{getValueDriverLabel(requestDetails.valueDriver)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Submission Details - Show technical fields if they exist */}
              {(requestDetails.requestPhase || requestDetails.gemUrl || requestDetails.repoUrl || requestDetails.deploymentPlan || requestDetails.technicalDetails || requestDetails.existingToolId) && (
                <div className="garage-request-detail-section">
                  <h3>Submission Details</h3>
                  <div className="garage-detail-grid">
                    {/* Request Phase */}
                    {requestDetails.requestPhase && (
                      <div className="garage-detail-item" style={{ gridColumn: '1 / -1' }}>
                        <div className="garage-detail-label">
                          <Rocket size={16} />
                          Request Phase
                        </div>
                        <div className="garage-detail-value">{getRequestPhaseLabel(requestDetails.requestPhase)}</div>
                      </div>
                    )}

                    {/* App Type */}
                    {requestDetails.appType && (
                      <div className="garage-detail-item">
                        <div className="garage-detail-label">
                          <FileCode size={16} />
                          Type
                        </div>
                        <div className="garage-detail-value">
                          {getAppTypeLabel(requestDetails.appType)}
                          {requestDetails.appType === 'app' && requestDetails.appSubtype
                            ? ` · ${requestDetails.appSubtype === 'bot' ? 'Bot' : 'Web'}`
                            : ''}
                        </div>
                      </div>
                    )}

                    {/* Web URL */}
                    {requestDetails.webUrl && (
                      <div className="garage-detail-item" style={{ gridColumn: '1 / -1' }}>
                        <div className="garage-detail-label">
                          <ExternalLink size={16} /> Web URL
                        </div>
                        <div className="garage-detail-value">
                          <a href={requestDetails.webUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--garage-orange)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {requestDetails.webUrl}
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )}

                    {/* RC App URL */}
                    {requestDetails.rcAppUrl && (
                      <div className="garage-detail-item" style={{ gridColumn: '1 / -1' }}>
                        <div className="garage-detail-label">
                          <ExternalLink size={16} /> RC App URL
                        </div>
                        <div className="garage-detail-value">
                          <a href={requestDetails.rcAppUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--garage-orange)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {requestDetails.rcAppUrl}
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Gem URL */}
                    {requestDetails.gemUrl && (
                      <div className="garage-detail-item" style={{ gridColumn: '1 / -1' }}>
                        <div className="garage-detail-label">
                          <ExternalLink size={16} />
                          Gem URL
                        </div>
                        <div className="garage-detail-value">
                          <a href={requestDetails.gemUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--garage-orange)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {requestDetails.gemUrl}
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Repository URL */}
                    {requestDetails.repoUrl && (
                      <div className="garage-detail-item" style={{ gridColumn: '1 / -1' }}>
                        <div className="garage-detail-label">
                          <Link2 size={16} />
                          Repository URL
                        </div>
                        <div className="garage-detail-value">
                          <a href={requestDetails.repoUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--garage-orange)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {requestDetails.repoUrl}
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Existing Tool Reference */}
                    {requestDetails.existingToolId && (
                      <div className="garage-detail-item" style={{ gridColumn: '1 / -1' }}>
                        <div className="garage-detail-label">
                          <Wrench size={16} />
                          Existing Tool
                        </div>
                        <div className="garage-detail-value">
                          {requestDetails.existingToolTitle ? (
                            <a
                              href={`/ai-garage/discover/${requestDetails.existingToolId}`}
                              style={{ color: 'var(--garage-orange)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                            >
                              {requestDetails.existingToolTitle}
                              <ExternalLink size={12} />
                            </a>
                          ) : (
                            `Tool ID: ${requestDetails.existingToolId}`
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Deployment Plan - Full width text area */}
                  {requestDetails.deploymentPlan && (
                    <div style={{ marginTop: '16px' }}>
                      <div className="garage-detail-label" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Rocket size={16} />
                        Deployment Plan / Instructions
                      </div>
                      <div style={{
                        background: 'var(--garage-surface)',
                        border: '1px solid var(--garage-border)',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        lineHeight: '1.6',
                        color: 'var(--garage-text-2)'
                      }}>
                        {requestDetails.deploymentPlan}
                      </div>
                    </div>
                  )}

                  {/* Technical Details - Full width text area */}
                  {requestDetails.technicalDetails && (
                    <div style={{ marginTop: '16px' }}>
                      <div className="garage-detail-label" style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FileCode size={16} />
                        Technical Details
                      </div>
                      <div style={{
                        background: 'var(--garage-surface)',
                        border: '1px solid var(--garage-border)',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        lineHeight: '1.6',
                        color: 'var(--garage-text-2)'
                      }}>
                        {requestDetails.technicalDetails}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Assigned Maker */}
              {requestDetails.assignedMakerEmail && (
                <div className="garage-request-detail-section">
                  <h3>Assigned Maker</h3>
                  <div className="garage-detail-maker-card">
                    <div className="garage-detail-maker-avatar">
                      {requestDetails.assignedMakerEmail.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="garage-detail-maker-info">
                      <div className="garage-detail-maker-name">{requestDetails.assignedMakerEmail}</div>
                      {requestDetails.assignedAt && (
                        <div className="garage-detail-maker-date">
                          Assigned <TimestampDisplay timestamp={requestDetails.assignedAt} userEmail={userEmail} showTime={true} showTimezone={true} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Context */}
              {requestDetails.implementationNotes && (
                <div className="garage-request-detail-section">
                  <h3>Additional Context</h3>
                  <div className="garage-detail-context-box">
                    <p>{requestDetails.implementationNotes}</p>
                  </div>
                </div>
              )}

              {/* Comments Section */}
              <div className="garage-request-comments">
                <h3>
                  <MessageCircle size={20} />
                  Comments ({comments.length})
                </h3>

                {/* Comment Form */}
                <form onSubmit={handleSubmitComment} className="garage-comment-form">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={4}
                  />
                  <div className="garage-comment-form-actions">
                    <button
                      type="submit"
                      className="garage-btn garage-btn-primary"
                      disabled={submittingComment || !newComment.trim()}
                    >
                      <Send size={16} />
                      {submittingComment ? 'Posting...' : 'Post Comment'}
                    </button>
                  </div>
                </form>

                {/* Comments List */}
                <div className="garage-comments-list">
                  {comments.length === 0 ? (
                    <div className="garage-comments-empty">
                      No comments yet. Be the first to comment!
                    </div>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} className="garage-comment-item">
                        <div className="garage-comment-header">
                          <div className="garage-comment-avatar">
                            {comment.userName?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="garage-comment-author-info">
                            <strong>{comment.userName || comment.userEmail}</strong>
                            <span className="garage-comment-date">
                              <TimestampDisplay timestamp={comment.createdAt} userEmail={userEmail} showRelative={true} />
                            </span>
                          </div>
                        </div>
                        <p
                          className="garage-comment-text"
                          dangerouslySetInnerHTML={{ __html: formatCommentHtml(comment.text) }}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Request Info Modal */}
      {requestInfoModalOpen && requestInfoIdea && (
        <RequestInfoModal
          isOpen={requestInfoModalOpen}
          idea={requestInfoIdea}
          adminEmail={userEmail}
          adminName={userName}
          onClose={() => {
            setRequestInfoModalOpen(false);
            setRequestInfoIdea(null);
          }}
          onSuccess={() => {
            loadIdeas();
            loadStats();
          }}
        />
      )}

      {/* Route to IT Modal */}
      {routeToITModalOpen && routeToITIdea && (
        <RouteToITModal
          isOpen={routeToITModalOpen}
          idea={routeToITIdea}
          adminEmail={userEmail}
          adminName={userName}
          onClose={() => {
            setRouteToITModalOpen(false);
            setRouteToITIdea(null);
          }}
          onSuccess={() => {
            loadIdeas();
            loadStats();
            if (selectedRequest === routeToITIdea.id) {
              loadRequestDetails(routeToITIdea.id);
            }
          }}
        />
      )}

      {/* Escalate to DOT Modal */}
      {escalateDotModalOpen && escalateDotIdea && (
        <EscalateToDotModal
          isOpen={escalateDotModalOpen}
          idea={escalateDotIdea}
          adminEmail={userEmail}
          adminName={userName}
          onClose={() => {
            setEscalateDotModalOpen(false);
            setEscalateDotIdea(null);
          }}
          onBackground={(ideaId) => {
            setPendingDotReviewIdeaId(ideaId);
            setEscalateDotModalOpen(false);
          }}
          onSuccess={() => {
            loadIdeas();
            loadStats();
            setPendingDotReviewIdeaId(null);
          }}
        />
      )}

      {/* Publish Gem Modal */}
      {publishGemModalOpen && publishGemIdea && (
        <PublishGemModal
          isOpen={publishGemModalOpen}
          idea={publishGemIdea}
          adminEmail={userEmail}
          adminName={userName}
          onClose={() => {
            setPublishGemModalOpen(false);
            setPublishGemIdea(null);
          }}
          onSuccess={(result) => {
            loadIdeas();
            loadStats();
            setAlertModal({
              isOpen: true,
              title: result?.alreadyPublished ? 'Already published' : 'Gem published',
              message: result?.alreadyPublished
                ? 'This request was already published to Discover.'
                : `“${result?.tool?.title || 'Gem'}” is now live in Discover.`,
              variant: 'info',
            });
          }}
        />
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Helper Tour — only after onboarding is done */}
      {showTour && !showOnboarding && (
        <TourGuide
          steps={REQUESTS_TOUR_STEPS}
          storageKey="tour-completed-requests"
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}
    </div>
  );
};

export default GarageRequestsView;
